import * as THREE from 'three';
import { ROSELoader } from './rose';

/**
 * Mesh format flags
 * @enum {Number}
 */
export const FORMAT = {
  NONE:         1 << 0,
  POSITION:     1 << 1,
  NORMAL:       1 << 2,
  COLOUR:       1 << 3,
  BLEND_WEIGHT: 1 << 4,
  BLEND_INDEX:  1 << 5,
  TANGENT:      1 << 6,
  UV1:          1 << 7,
  UV2:          1 << 8,
  UV3:          1 << 9,
  UV4:          1 << 10
};

/**
 * @property {{min: Vector3, max: Vector3}} bounds
 * @property {Float32Array} vertices
 * @property {Float32Array?} normals
 * @property {Float32Array?} tangents
 * @property {Float32Array?} colours
 * @property {Float32Array?} faces
 * @property {Float32Array?} skinWeights
 * @property {Float32Array?} skinIndices
 * @property {Float32Array[]?} uv
 */
export class Mesh {
  // Static reference to enum for backwards compatibility
  static FORMAT = FORMAT;

  bounds: any = {};
  vertices: Float32Array | null = null;
  normals: Float32Array | null = null;
  tangents: Float32Array | null = null;
  colours: Float32Array | null = null;
  faces: Uint16Array | null = null;
  skinWeights: Float32Array | null = null;
  skinIndices: Uint16Array | null = null;
  uv: Float32Array[] = [];

  static loadMesh8(rh: any): Mesh {
    let bones, boneTable: number[], faces, format, i, vertices;
    let data = new Mesh();

    format = rh.readUint32();
    data.bounds.min = rh.readVector3();
    data.bounds.max = rh.readVector3();

    bones = rh.readUint16();
    boneTable = [];
    for (i = 0; i < bones; ++i) {
      boneTable.push(rh.readUint16());
    }

    vertices = rh.readUint16();
    data.vertices = rh.readFloatArray(3 * vertices);

    if (format & FORMAT.NORMAL) {
      data.normals = rh.readFloatArray(3 * vertices);
    }

    if (format & FORMAT.COLOUR) {
      data.colours = rh.readFloatArray(4 * vertices);
    }

    if (format & (FORMAT.BLEND_INDEX | FORMAT.BLEND_WEIGHT)) {
      let skinIdx = 0;
      data.skinWeights = new Float32Array(4 * vertices);
      data.skinIndices = new Uint16Array(4 * vertices);

      for (i = 0; i < vertices; ++i) {
        data.skinWeights[skinIdx + 0] = rh.readFloat();
        data.skinWeights[skinIdx + 1] = rh.readFloat();
        data.skinWeights[skinIdx + 2] = rh.readFloat();
        data.skinWeights[skinIdx + 3] = rh.readFloat();

        data.skinIndices[skinIdx + 0] = boneTable[rh.readUint16()];
        data.skinIndices[skinIdx + 1] = boneTable[rh.readUint16()];
        data.skinIndices[skinIdx + 2] = boneTable[rh.readUint16()];
        data.skinIndices[skinIdx + 3] = boneTable[rh.readUint16()];

        skinIdx += 4;
      }
    }

    if (format & FORMAT.TANGENT) {
      data.tangents = rh.readFloatArray(3 * vertices);
    }

    if (format & FORMAT.UV1) {
      data.uv[0] = rh.readFloatArray(2 * vertices);
    }

    if (format & FORMAT.UV2) {
      data.uv[1] = rh.readFloatArray(2 * vertices);
    }

    if (format & FORMAT.UV3) {
      data.uv[2] = rh.readFloatArray(2 * vertices);
    }

    if (format & FORMAT.UV4) {
      data.uv[2] = rh.readFloatArray(2 * vertices);
    }

    faces = rh.readUint16();
    data.faces = rh.readUint16Array(3 * faces);

    return data;
  }

  static loadMesh6(rh: any): Mesh {
    let bones, boneTable: number[], faces, format, i, idx, vertices;
    const data = new Mesh();

    format = rh.readUint32();
    data.bounds.min = rh.readVector3();
    data.bounds.max = rh.readVector3();

    bones = rh.readUint32();
    boneTable = [];
    for (i = 0; i < bones; ++i) {
      rh.skip(4);
      boneTable.push(rh.readUint32());
    }

    vertices = rh.readUint32();
    data.vertices = new Float32Array(3 * vertices);
    for (i = 0, idx = 0; i < vertices; i += 1, idx += 3) {
      rh.skip(4);
      data.vertices[idx    ] = rh.readFloat() * ZZ_SCALE_IN;
      data.vertices[idx + 1] = rh.readFloat() * ZZ_SCALE_IN;
      data.vertices[idx + 2] = rh.readFloat() * ZZ_SCALE_IN;
    }

    if (format & FORMAT.NORMAL) {
      data.normals = new Float32Array(3 * vertices);
      for (i = 0, idx = 0; i < vertices; i += 1, idx += 3) {
        rh.skip(4);
        data.normals[idx + 0] = rh.readFloat();
        data.normals[idx + 1] = rh.readFloat();
        data.normals[idx + 2] = rh.readFloat();
      }
    }

    if (format & FORMAT.COLOUR) {
      data.colours = new Float32Array(4 * vertices);
      for (i = 0, idx = 0; i < vertices; i += 1, idx += 4) {
        rh.skip(4);
        data.colours[idx + 0] = rh.readFloat();
        data.colours[idx + 1] = rh.readFloat();
        data.colours[idx + 2] = rh.readFloat();
        data.colours[idx + 3] = rh.readFloat();
      }
    }

    if (format & (FORMAT.BLEND_INDEX | FORMAT.BLEND_WEIGHT)) {
      data.skinWeights = new Float32Array(4 * vertices);
      data.skinIndices = new Uint16Array(4 * vertices);

      for (i = 0, idx = 0; i < vertices; i += 1, idx += 4) {
        rh.skip(4);
        data.skinWeights[idx + 0] = rh.readFloat();
        data.skinWeights[idx + 1] = rh.readFloat();
        data.skinWeights[idx + 2] = rh.readFloat();
        data.skinWeights[idx + 3] = rh.readFloat();

        data.skinIndices[idx + 0] = boneTable[rh.readUint32()];
        data.skinIndices[idx + 1] = boneTable[rh.readUint32()];
        data.skinIndices[idx + 2] = boneTable[rh.readUint32()];
        data.skinIndices[idx + 3] = boneTable[rh.readUint32()];
      }
    }

    if (format & FORMAT.TANGENT) {
      data.tangents = new Float32Array(3 * vertices);
      for (i = 0, idx = 0; i < vertices; i += 1, idx += 3) {
        rh.skip(4);
        data.tangents[idx + 0] = rh.readFloat();
        data.tangents[idx + 1] = rh.readFloat();
        data.tangents[idx + 2] = rh.readFloat();
      }
    }

    if (format & FORMAT.UV1) {
      data.uv[0] = new Float32Array(2 * vertices);
      for (i = 0, idx = 0; i < vertices; i += 1, idx += 2) {
        rh.skip(4);
        data.uv[0][idx + 0] = rh.readFloat();
        data.uv[0][idx + 1] = rh.readFloat();
      }
    }

    if (format & FORMAT.UV2) {
      data.uv[1] = new Float32Array(2 * vertices);
      for (i = 0, idx = 0; i < vertices; i += 1, idx += 2) {
        rh.skip(4);
        data.uv[1][idx + 0] = rh.readFloat();
        data.uv[1][idx + 1] = rh.readFloat();
      }
    }

    if (format & FORMAT.UV3) {
      data.uv[2] = new Float32Array(2 * vertices);
      for (i = 0, idx = 0; i < vertices; i += 1, idx += 2) {
        rh.skip(4);
        data.uv[2][idx + 0] = rh.readFloat();
        data.uv[2][idx + 1] = rh.readFloat();
      }
    }

    if (format & FORMAT.UV4) {
      data.uv[3] = new Float32Array(2 * vertices);
      for (i = 0, idx = 0; i < vertices; i += 1, idx += 2) {
        rh.skip(4);
        data.uv[3][idx + 0] = rh.readFloat();
        data.uv[3][idx + 1] = rh.readFloat();
      }
    }

    faces = rh.readUint32();
    data.faces = new Uint16Array(3 * faces);
    for (i = 0, idx = 0; i < faces; i += 1, idx += 3) {
      rh.skip(4);
      data.faces[idx + 0] = rh.readUint32();
      data.faces[idx + 1] = rh.readUint32();
      data.faces[idx + 2] = rh.readUint32();
    }

    return data;
  }

  createBufferGeometry(): any {
    let geometry;
    geometry = new THREE.BufferGeometry();

    geometry.setAttribute('position', new THREE.BufferAttribute(this.vertices, 3));
    geometry.setIndex(new THREE.BufferAttribute(this.faces, 1));

    if (this.normals) {
      geometry.setAttribute('normal', new THREE.BufferAttribute(this.normals, 3));
    }

    if (this.skinWeights) {
      geometry.setAttribute('skinWeight', new THREE.BufferAttribute(this.skinWeights, 4));
    }

    if (this.skinIndices) {
      geometry.setAttribute('skinIndex', new THREE.BufferAttribute(this.skinIndices, 4));
    }

    if (this.uv[0]) {
      geometry.setAttribute('uv', new THREE.BufferAttribute(this.uv[0], 2));
    }

    if (this.uv[1]) {
      geometry.setAttribute('uv1', new THREE.BufferAttribute(this.uv[1], 2));
    }

    if (this.uv[2] || this.uv[3]) {
      throw 'BufferGeometry does not support more than 2 uv channels!';
    }

    const vertexCount = this.vertices!.length / 3;
    const alphaArray = new Float32Array(vertexCount);
    for (let i = 0; i < vertexCount; i++) {
      alphaArray[i] = 1.0;
    }
    geometry.setAttribute('alpha', new THREE.BufferAttribute(alphaArray, 1));

    geometry.dynamic = false;
    geometry.computeBoundingSphere();
    if (!this.normals) {
      geometry.computeVertexNormals();
    }
    return geometry;
  }

  /**
   * Parse a ZMS mesh from a BinaryReader and return a BufferGeometry.
   */
  static parseMesh(rh: any, sourcePath?: string): any {
    let version, magic, mesh;

    magic = rh.readStrLen(7);
    rh.skip(1);

    if (magic === 'ZMS0005') {
      version = 5;
    } else if (magic === 'ZMS0006') {
      version = 6;
    } else if (magic === 'ZMS0007') {
      version = 7;
    } else if (magic === 'ZMS0008') {
      version = 8;
    } else {
      throw new Error('Unexpected ZMS magic header ' + magic + (sourcePath ? ' in ' + sourcePath : ''));
    }

    if (version >= 7) {
      mesh = Mesh.loadMesh8(rh);
    } else {
      mesh = Mesh.loadMesh6(rh);
    }

    const geometry = mesh.createBufferGeometry();
    if (sourcePath) {
      geometry.path = sourcePath;
    }
    return geometry;
  }

  static load(path: any, callback: any, onError?: (err: any) => void) {
    ROSELoader.load(path, function(rh: any) {
      const geometry = Mesh.parseMesh(rh, path);
      callback(geometry);
    }, onError);
  }
}
